import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ErrorComponent } from '../ErrorComponent'
import { Loading } from '../Loading'
import { TeamMemberCard } from '../TeamMemberCard/TeamMemberCard'

type MessagesApplicationsApplicationTeam = RestSchemas['Messages.Applications.ApplicationTeam']
type MessagesSubscriptionsOrganisationTeamUser = RestSchemas['Messages.Subscriptions.OrganisationTeamUser']

export const MaintainSupportTeam = ({
  selectedSupportTeam,
}: {
  selectedSupportTeam?: MessagesApplicationsApplicationTeam
}) => {
  const queryClient = useQueryClient()

  const [supportTeamUsers, setSupportTeamUsers] = useState<MessagesSubscriptionsOrganisationTeamUser[]>([])
  const [currentTeamUsers, setCurrentTeamUsers] = useState<MessagesSubscriptionsOrganisationTeamUser[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const {
    getApplicationsApiGetApplicationSupportTeamUsersForOrg,
    getSubscriptionsApiListTeamUsers,
    getApplicationsApiGetApplicationTeamsForUserOrganization,
    postSubscriptionsApiAddTeamUser,
    deleteSubscriptionsApiDeleteTeamUser,
  } = createRestClient()

  const {
    data: supportTeamUsersData = [],
    isPending: isSupportTeamUsersPending,
    isError: isSupportTeamUsersError,
  } = getApplicationsApiGetApplicationSupportTeamUsersForOrg.useQuery<MessagesSubscriptionsOrganisationTeamUser[]>(
    { path: { appId: selectedSupportTeam?.applicationId || 0 } },
    {
      select: (data) => {
        const seenUserIds = new Set<number>()
        return data.filter((user) => {
          const isInCurrentTeam = user.teamId === selectedSupportTeam?.id && user.teamName === selectedSupportTeam?.name
          const isNewUser = !seenUserIds.has(user.userId)

          if (!isInCurrentTeam && isNewUser && user.userId !== 0) {
            seenUserIds.add(user.userId)
            return true
          }
          return false
        })
      },
      enabled: !!selectedSupportTeam?.applicationId,
    }
  )

  const {
    data: currentTeamUsersData = [],
    isPending: isTeamMembersPending,
    isError: isTeamMembersError,
  } = getSubscriptionsApiListTeamUsers.useQuery<MessagesSubscriptionsOrganisationTeamUser[]>(
    { query: { teamId: selectedSupportTeam?.id } },
    {
      select: (data) =>
        data.filter(
          (user) =>
            user.userId && user.userId !== 0 && user.id && user.id !== 0 && user.teamId === selectedSupportTeam?.id
        ),
      enabled: !!selectedSupportTeam?.id,
    }
  )

  useEffect(() => {
    setSupportTeamUsers(supportTeamUsersData)
    setCurrentTeamUsers(currentTeamUsersData)
  }, [supportTeamUsersData, currentTeamUsersData])

  const invalidateQueries = () => {
    getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries(
      { parameters: { path: { appId: selectedSupportTeam?.applicationId || 0 } } },
      queryClient
    )
    getSubscriptionsApiListTeamUsers.invalidateQueries(
      { parameters: { query: { teamId: selectedSupportTeam?.id } } },
      queryClient
    )
    getApplicationsApiGetApplicationSupportTeamUsersForOrg.invalidateQueries(
      { parameters: { path: { appId: selectedSupportTeam?.applicationId || 0 } } },
      queryClient
    )
  }

  const { mutateAsync: addTeamUser } = postSubscriptionsApiAddTeamUser.useMutation()
  const { mutate: deleteTeamUser } = deleteSubscriptionsApiDeleteTeamUser.useMutation()

  const onOptionSelect = (_: SelectionEvents, data: OptionOnSelectData) => {
    setSelectedUserIds(data.selectedOptions)
  }

  const onAddSubmit = async () => {
    const selectedUsers = supportTeamUsers.filter((user) => selectedUserIds.includes(user.userId.toString()))

    await Promise.all(
      selectedUsers.map((member) =>
        addTeamUser(
          {
            body: {
              ...member,
              teamId: selectedSupportTeam?.id ?? 0,
              applicationId: selectedSupportTeam?.applicationId ?? 0,
              profilePicture: 'No profile pic submitted',
            },
          },
          {
            onError: (error) => {
              toast.error('Error adding team member')
              console.error(error)
            },
            onSuccess: () => {
              toast.success('Team member added')
            },
          }
        )
      )
    )

    setSelectedUserIds([])
    invalidateQueries()
  }

  const onDelete = (member: MessagesSubscriptionsOrganisationTeamUser) => {
    deleteTeamUser(
      {
        body: {
          ...member,
          teamId: selectedSupportTeam?.id ?? 0,
        },
      },
      {
        onError: (error) => {
          toast.error('Error deleting team member')
          console.error(error)
        },
        onSuccess: () => {
          toast.success('Team member deleted')
        },
        onSettled: () => {
          setSelectedUserIds([])
          invalidateQueries()
        },
      }
    )
  }

  if (isSupportTeamUsersPending || isTeamMembersPending) return <Loading />

  if (isSupportTeamUsersError || isTeamMembersError) return <ErrorComponent />

  const selectedUsers = supportTeamUsers.filter((user) => selectedUserIds.includes(user.userId.toString()))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
      <Dropdown
        aria-labelledby={'add-support-user-to-team'}
        multiselect
        placeholder="Add support team members"
        selectedOptions={selectedUserIds}
        value={selectedUsers.map((u) => `${u.givenName ?? ''} ${u.familyName ?? ''}`.trim()).join(', ')}
        onOptionSelect={onOptionSelect}
      >
        <div style={{ maxHeight: '600px', overflowY: 'scroll' }}>
          {supportTeamUsers.map((option) => (
            <Option
              key={option.userId}
              value={option.userId.toString()}
              disabled={currentTeamUsers.some(
                (user) => user.userId === option.userId && user.teamId === selectedSupportTeam?.id
              )}
            >
              {option.givenName || option.familyName
                ? `${option.givenName ?? ''} ${option.familyName ?? ''}`.trim()
                : (option.email ?? 'Unknown User')}
            </Option>
          ))}
        </div>
      </Dropdown>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button text="Add members" onClick={onAddSubmit} />
      </div>

      {currentTeamUsers?.map((user) => (
        <TeamMemberCard
          key={`${user.id}-${user.teamId}-${user.userId}`}
          onDelete={() => onDelete(user)}
          userId={user.userId}
          teamUsers={currentTeamUsers}
          teamId={user.teamId}
          teamName={user.teamName}
        />
      ))}
    </div>
  )
}
